/*
 * Copyright (c) Nexign, JSC, 2023.
 */
/**
 *
 *
 * @author Evgeniy Naumov
 * @version 1.27.0
 * @since 1.25.1
 */

import { FC, memo, useEffect } from 'react';

import { Button, Typography } from '@nexign/scc-ui-kit';
import { Form, Formik } from 'formik';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';

import ArrowTooltip from '@/main/components/common/Tooltip/Tooltip';
import { appHistoryPush } from '@/main/modules/app/appHistory';
import FormFieldDate from '@/main/modules/form/components/FormFieldDate';
import FormFieldInput from '@/main/modules/form/components/FormFieldInput';
import {
    makeSelectPageNewsTranslation,
    makeSelectTranslationCommon,
} from '@/main/modules/internationalization/intSelectors';
import { KitLoader } from '@/main/modules/kitLoader';
import { modalSetStatusAction } from '@/main/modules/modal/modalActions';
import { EModalStatus } from '@/main/modules/modal/types/modalTypes';
import { EFormatData } from '@/main/pages/PageDashboard/types/widgetTypes';
import { RoutePath } from '@/main/routes/constants/routesConstants';
import { FormSubmitHandler } from '@/main/types/globalTypes';

import {
    ECreateNewsTypes,
    ENewsFormCreationFields,
    TODAY_FULL_DATE,
    defaultCreateExternalNewsFormValues,
} from '../../constants';
import { usePublishExternalNewsItemMutation } from '../../store';
import {
    ButtonsTooltipWrapperStyled,
    ButtonsWrapperStyled,
    NewsFormControlsContainerStyled,
    NewsFormDateContainerStyled,
    RequiredHintAsteriskStyled,
    RequiredHintStyled,
} from '../../styled/PageNewsCreateStyled';
import { ICreateExternalNewsForm } from '../../types';
import { getNewsExternalFormValidationSchema } from '../../validation';
import { useOpenPublishModal } from './hooks/useOpenPublishModal';

interface INewsExternalFormProps {
    handleCancel: () => void;
    handleRedirect: () => void;
}

const NewsExternalForm: FC<INewsExternalFormProps> = ({ handleRedirect, handleCancel }) => {
    const dispatch = useDispatch();

    const {
        createNews: { requiredHint: tRequiredHint, form: tForm },
    } = useSelector(makeSelectPageNewsTranslation());
    const tCorporate = useSelector(makeSelectTranslationCommon());

    const [publishExternalNews, { isLoading, isError }] = usePublishExternalNewsItemMutation();

    const openPublishModal = useOpenPublishModal();

    const validationSchema = getNewsExternalFormValidationSchema(tCorporate.form);

    const handleSubmit: FormSubmitHandler<ICreateExternalNewsForm> = (data, { setSubmitting, resetForm }) => {
        const handleCreate = () => {
            publishExternalNews({
                title: data[ENewsFormCreationFields.TITLE],
                link: data[ENewsFormCreationFields.LINK],
                publishFrom: moment(data[ENewsFormCreationFields.PUBLISH_FROM]).format(EFormatData.FullData),
                publishTo: data[ENewsFormCreationFields.PUBLISH_TO]
                    ? moment(data[ENewsFormCreationFields.PUBLISH_TO]).format(EFormatData.FullData)
                    : undefined,
            })
                .unwrap()
                .then(() => {
                    appHistoryPush(RoutePath.news);
                    resetForm();
                })
                .catch(() => {
                    setSubmitting(false);
                });
        };
        openPublishModal(ECreateNewsTypes.EXTERNAL, EModalStatus.start, { params: data }, handleCreate, setSubmitting);
    };

    useEffect(() => {
        if (isError) {
            dispatch(
                modalSetStatusAction({
                    status: EModalStatus.failure,
                }),
            );
        }
    }, [dispatch, isError]);

    return (
        <>
            <KitLoader showLoader={isLoading} variant="global" />
            <Formik<ICreateExternalNewsForm>
                enableReinitialize
                initialValues={defaultCreateExternalNewsFormValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {({ isValid, isValidating, isSubmitting, dirty, values }) => {
                    const submitDisabled = isSubmitting || isValidating || !isValid || !dirty;

                    const publishFromMaxDate = !!values[ENewsFormCreationFields.PUBLISH_TO]
                        ? moment(values[ENewsFormCreationFields.PUBLISH_TO]).subtract(1, 'day')
                        : moment(TODAY_FULL_DATE).add(100, 'year');

                    const publishFromMinDate = moment(TODAY_FULL_DATE);

                    const publishToMaxDate = moment(TODAY_FULL_DATE).add(100, 'year');

                    const publishToMinDate = !!values[ENewsFormCreationFields.PUBLISH_FROM]
                        ? moment(values[ENewsFormCreationFields.PUBLISH_FROM]).add(1, 'day')
                        : moment(TODAY_FULL_DATE).add(1, 'day');

                    return (
                        <Form>
                            <FormFieldInput
                                required
                                dataName="newsCreateExternal-title"
                                label={tForm.fields.title.title}
                                labelHint={<Typography variant="body2">{tForm.fields.title.hint}</Typography>}
                                name={ENewsFormCreationFields.TITLE}
                            />
                            <FormFieldInput
                                required
                                dataName="newsCreateExternal-link"
                                label={tForm.fields.link.title}
                                name={ENewsFormCreationFields.LINK}
                                placeholder={tForm.fields.link.placeholder}
                            />
                            <NewsFormDateContainerStyled>
                                <FormFieldDate
                                    required
                                    dataName="newsCreateExternal-fromDate"
                                    headType="separate"
                                    label={tForm.fields.publishFrom}
                                    maxDate={publishFromMaxDate}
                                    minDate={publishFromMinDate}
                                    name={ENewsFormCreationFields.PUBLISH_FROM}
                                />
                                <FormFieldDate
                                    dataName="newsCreateExternal-toDate"
                                    disabled={!values[ENewsFormCreationFields.PUBLISH_FROM]}
                                    headType="separate"
                                    label={tForm.fields.publishTo}
                                    maxDate={publishToMaxDate}
                                    minDate={publishToMinDate}
                                    name={ENewsFormCreationFields.PUBLISH_TO}
                                />
                            </NewsFormDateContainerStyled>

                            <NewsFormControlsContainerStyled>
                                <RequiredHintStyled>
                                    <RequiredHintAsteriskStyled>*</RequiredHintAsteriskStyled>
                                    <Typography variant="h5">{tRequiredHint}</Typography>
                                </RequiredHintStyled>
                                <ButtonsWrapperStyled>
                                    <Button color="info" variant="text" onClick={dirty ? handleCancel : handleRedirect}>
                                        {tForm.controls.cancel}
                                    </Button>
                                    <ArrowTooltip disableHoverListener={!submitDisabled} title={tForm.notValid}>
                                        <ButtonsTooltipWrapperStyled>
                                            <Button
                                                disabled={submitDisabled}
                                                size="small"
                                                type="submit"
                                                typographyProps={{ variant: 'h5' }}
                                            >
                                                {tForm.controls.submit}
                                            </Button>
                                        </ButtonsTooltipWrapperStyled>
                                    </ArrowTooltip>
                                </ButtonsWrapperStyled>
                            </NewsFormControlsContainerStyled>
                        </Form>
                    );
                }}
            </Formik>
        </>
    );
};

export default memo(NewsExternalForm);
